<template>
    <app-layout>
        <template #header>
            <h2 class="font-semibold text-xl text-gray-800 leading-tight">
                Manage Publications
            </h2>
        </template>

        <div>
            <div class="max-w-7xl mx-auto py-10 sm:px-6 lg:px-8">
                <FormSection @submitted="createPublication">
                    <template #title>
                        Create a new publication
                    </template>

                    <template #description>
                        Create a new publications which will be listed in the Research tab.
                    </template>

                    <template #form>
                        <div class="col-span-6 sm:col-span-4">
                            <Label for="title" value="Publication Title" />

                            <Input id="title" type="text" class="mt-1 block w-full" v-model="createForm.title"
                                ref="title"
                            />

                            <InputError :message="createForm.errors.title" class="mt-2" />
                        </div>

                        <div class="col-span-6 sm:col-span-4">
                            <Label for="type" value="Publication Type" />

                            <Select id="type" class="mt-1 block w-full"
                                v-model="createForm.type"
                                ref="type"
                                :options="publicationTypes"
                                option-value="label"
                                option-key="value"
                                :default-text="null"
                            />

                            <InputError :message="createForm.errors.type" class="mt-2" />

                            <HelpText>
                                Under which type of publication does the publication fall?
                            </HelpText>
                        </div>

                        <div class="col-span-6 sm:col-span-4">
                            <Label for="number" value="Publication Number" />

                            <Input id="number" type="text" class="mt-1 block w-full" v-model="createForm.number"
                                ref="number"
                            />
                            <InputError :message="createForm.errors.number" class="mt-2" />

                            <HelpText>
                                Under which number within its type is the publication listed?<br>
                                <span class="text-red-500">Enter 0 for <span class="font-bold">published articles</span> and <span class="font-bold">further papers</span>!</span>
                            </HelpText>
                        </div>

                        <div class="col-span-6 sm:col-span-4">
                            <Label for="url" value="Publication URL" />

                            <Input id="url" type="text" class="mt-1 block w-full" v-model="createForm.url"
                                ref="url"
                            />
                            <InputError :message="createForm.errors.url" class="mt-2" />

                            <HelpText>
                                The url of the publication on the https://g2lm-lic.iza.org/ website.
                            </HelpText>
                        </div>

                        <div class="col-span-6 sm:col-span-4">
                            <div class="block mt-1 w-full">
                                <label class="flex items-center">
                                    <Checkbox name="is_special" v-model="createForm.is_published" />
                                    <span class="ml-2 text-sm text-gray-600">If checked, the publication will be listed.</span>
                                </label>

                                <InputError :message="createForm.errors.is_published" class="mt-2" />
                            </div>
                        </div>
                    </template>

                    <template #actions>
                        <ActionMessage :on="createForm.recentlySuccessful" class="mr-3">
                            Created.
                        </ActionMessage>

                        <ActionMessage :on="createForm.processing" class="mr-3">
                            Creating...
                        </ActionMessage>

                        <Button :class="{ 'opacity-25': createForm.processing }"
                            :disabled="createForm.processing"
                        >
                            Create
                        </Button>
                    </template>
                </FormSection>

                <SectionBorder />

                <table
                    class="w-full whitespace-nowrap rounded bg-white divide-y divide-gray-300 overflow-hidden shadow"
                >
                    <thead class="bg-gray-900">
                    <tr class="text-white">
                        <th class="font-semibold text-sm uppercase px-6 py-4 text-left">Title</th>
                        <th class="font-semibold text-sm uppercase px-6 py-4 text-right">Listed?</th>
                        <th class="font-semibold text-sm uppercase px-6 py-4 text-right w-1">&nbsp;</th>
                    </tr>
                    </thead>

                    <tbody class="divide-y divide-gray-200">
                    <tr v-for="publication in publications" :key="publication.id">
                        <td class="px-6 py-4">
                            <div class="flex items-center">
                                <div class="text-sm font-semibold text-gray-900">
                                    <div class="text-xs text-gray-500">{{ getTypeTagLine(publication) }}</div>

                                    <button class="hover:underline" type="button" @click="editPublication(publication)">
                                        {{ publication.title }}
                                    </button>
                                </div>
                            </div>
                        </td>

                        <td class="px-6 py-4 text-right">
                            <span class="text-sm font-semibold text-gray-900">
                                <font-awesome-icon
                                    v-if="publication.is_published"
                                    :icon="['fad', 'eye']"
                                    class="text-green-500"
                                ></font-awesome-icon>

                                <font-awesome-icon
                                    v-else
                                    :icon="['fad', 'eye-slash']"
                                    class="text-red-500"
                                ></font-awesome-icon>
                            </span>
                        </td>

                        <td class="px-6 py-4 text-left w-1">
                            <DangerButton @click.native="confirmDeletion(publication)" title="Delete" class="mr-2">
                                <font-awesome-icon
                                    :icon="['fad', 'trash']"
                                ></font-awesome-icon>
                            </DangerButton>

                            <SecondaryButton @click.native="editPublication(publication)" title="Edit">
                                <font-awesome-icon
                                    :icon="['fad', 'pencil']"
                                ></font-awesome-icon>
                            </SecondaryButton>
                        </td>
                    </tr>
                    </tbody>
                </table>

                <FormModal :show="editingModal" @submitted="submitEditForm">
                    <template #title>
                        Edit Publication
                    </template>

                    <template #content>
                        <div class="grid grid-cols-6 gap-6">
                            <div class="col-span-6">
                                <Label for="title" value="Publication Title" />

                                <Input id="title" type="text" class="mt-1 block w-full" v-model="form.title"
                                    ref="title"
                                />

                                <InputError :message="form.errors.title" class="mt-2" />
                            </div>

                            <div class="col-span-6">
                                <Label for="type" value="Publication Type" />

                                <Select id="type" class="mt-1 block w-full"
                                    v-model="form.type"
                                    ref="type"
                                    :options="publicationTypes"
                                    option-value="label"
                                    option-key="value"
                                    :default-text="null"
                                />

                                <InputError :message="form.errors.type" class="mt-2" />

                                <HelpText>
                                    Under which type of publication does the publication fall?
                                </HelpText>
                            </div>

                            <div class="col-span-6">
                                <Label for="number" value="Publication Number" />

                                <Input id="number" type="text" class="mt-1 block w-full" v-model="form.number"
                                    ref="number"
                                />
                                <InputError :message="form.errors.number" class="mt-2" />

                                <HelpText>
                                    Under which number within its type is the publication listed?
                                </HelpText>
                            </div>

                            <div class="col-span-6">
                                <Label for="url" value="Publication URL" />

                                <Input id="url" type="text" class="mt-1 block w-full" v-model="form.url"
                                    ref="url"
                                />
                                <InputError :message="form.errors.url" class="mt-2" />

                                <HelpText>
                                    The url of the publication on the https://g2lm-lic.iza.org/ website.
                                </HelpText>
                            </div>

                            <div class="col-span-6">
                                <div class="block mt-1 w-full">
                                    <label class="flex items-center">
                                        <Checkbox name="is_special" v-model="form.is_published" />
                                        <span class="ml-2 text-sm text-gray-600">If checked, the publication will be listed.</span>
                                    </label>

                                    <InputError :message="form.errors.is_published" class="mt-2" />
                                </div>
                            </div>
                        </div>
                    </template>

                    <template #footer>
                        <SecondaryButton @click.native="closeEditModal">
                            Nevermind
                        </SecondaryButton>

                        <Button type="submit"
                            :class="{ 'opacity-25': form.processing }"
                            :disabled="form.processing"
                        >
                            Save
                        </Button>
                    </template>
                </FormModal>

                <DialogModal :show="confirmingDeletion" @close="closeDeleteModal">
                    <template #title>
                        Delete Publication
                    </template>

                    <template #content>
                        Are you sure you want to remove this publication?
                    </template>

                    <template #footer>
                        <SecondaryButton @click.native="closeDeleteModal">
                            Nevermind
                        </SecondaryButton>

                        <DangerButton
                            class="ml-2"
                            @click.native="deletePublication"
                            :class="{ 'opacity-25': processing }"
                            :disabled="processing"
                        >
                            Remove Publication
                        </DangerButton>
                    </template>
                </DialogModal>
            </div>
        </div>
    </app-layout>
</template>

<script>
import AppLayout from '@/Layouts/AppLayout'
import JetInput from '@/Jetstream/Input'
import DangerButton from '@/Jetstream/DangerButton'
import SecondaryButton from '@/Jetstream/SecondaryButton'
import DialogModal from '@/Jetstream/DialogModal'
import FormModal from '@/Jetstream/FormModal'
import Button from '@/Jetstream/Button'
import Label from '@/Jetstream/Label'
import Input from '@/Jetstream/Input'
import InputError from '@/Jetstream/InputError'
import Textarea from '@/Jetstream/Textarea'
import SectionBorder from '@/Jetstream/SectionBorder'
import FormSection from '@/Jetstream/FormSection'
import ActionMessage from '@/Jetstream/ActionMessage'
import HelpText from '@/Jetstream/HelpText'
import Select from '@/Jetstream/Select'
import Checkbox from '@/Jetstream/Checkbox'

export default {
    components: {
        HelpText,
        ActionMessage,
        FormSection,
        SectionBorder,
        Textarea,
        InputError,
        Input,
        Label,
        Select,
        Button,
        Checkbox,
        FormModal,
        DialogModal,
        SecondaryButton,
        DangerButton,
        AppLayout,
        JetInput,
    },

    props: {
        publications: Array,
    },

    data () {
        return {
            editingModal: false,
            confirmingDeletion: false,

            selectedPublication: null,

            processing: false,

            publicationTypes: [
                { value: 'policy_brief', label: 'Policy Brief' },
                { value: 'synthesis_paper', label: 'Synthesis Paper' },
                { value: 'working_paper', label: 'Working Paper' },
                { value: 'published_articles', label: 'Published Articles' },
                { value: 'further_papers', label: 'Further Papers' },
            ],

            form: this.$inertia.form({
                title: '',
                number: '',
                url: '',
                type: '',
                is_published: true,
            }),

            createForm: this.$inertia.form({
                title: '',
                number: '',
                url: '',
                type: '',
                is_published: true,
            }),
        }
    },

    methods: {
        createPublication () {
            this.createForm.put(route('intern.publication.create'), {
                errorBag: 'createPublication',
                preserveScroll: true,
                onSuccess: () => {
                    this.createForm.reset()
                },
            })
        },

        getTypeTagLine (publication) {
            let line = ''

            switch (publication.type) {
                case 'policy_brief':
                    line = 'G²LM|LIC Policy Brief'
                    break
                case 'synthesis_paper':
                    line = 'G²LM|LIC Synthesis Paper'
                    break
                case 'working_paper':
                    line = 'G²LM|LIC Working Paper'
                    break
                case 'published_articles':
                    line = 'G²LM|LIC Published Article'

                    return line
                case 'further_papers':
                    line = 'Further Paper'

                    return line
            }

            return line + ' No. ' + publication.number
        },

        editPublication (publication) {
            this.selectedPublication = publication

            this.form.title = publication.title
            this.form.number = publication.number
            this.form.url = publication.url
            this.form.type = publication.type
            this.form.is_published = publication.is_published

            this.editingModal = true
        },

        submitEditForm () {
            this.form.post(route('intern.publication.update', this.selectedPublication.id), {
                errorBag: 'updatePublication',
                preserveScroll: true,
                onSuccess: () => {
                    this.closeEditModal()
                },
            })
        },

        confirmDeletion(sample) {
            this.selectedPublication = sample;
            this.confirmingDeletion = true;
        },

        closeDeleteModal() {
            this.confirmingDeletion = false
            this.selectedPublication = null
        },

        closeEditModal () {
            this.editingModal = false

            this.selectedPublication = null
            this.form.reset()
        },

        deletePublication() {
            this.processing = true

            this.$inertia.delete(route('intern.publication.destroy', this.selectedPublication.id), {
                onSuccess: () => {
                    this.processing = false

                    this.closeDeleteModal()
                },
                onError: () => {
                    this.processing = false
                }
            })
        }
    },
}
</script>
